import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosError,
  type AxiosProgressEvent,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import _get from 'lodash/get'

import { ConcurrencyLimit } from '@/lib/concurrency'
import DateUtils from '@/lib/date/date'
import ErrorHandler from '@/lib/error/handling'
import { retry } from '@/lib/error/retry'
import { isFakeDelays } from '@/lib/html/env'
import { getErrorCode } from '@/lib/http/codes'
import { objectToCamelCase, objectToSnakeCase, truncatePayload } from '@/lib/json/case'

import { MAX_GET_PARAM_LENGTH } from './use-glammatic-api.constants'
import {
  type WebsiteEvent,
  type WebsiteEventStatus,
  type PaginatedResponse,
  type Review,
  type Catalog,
  type StockImage,
  type BusinessFile,
  type CatalogCategory,
  type ReviewResponse,
  type ComponentConfiguration,
  type MerchantLocation,
  type ProviderServiceSetting,
  type ContentSuggestion,
  type WebsitePage,
  type WebsiteDetail,
  type QuestionGroup,
  type QuestionGroupQuestion,
  type GbpAccount,
  type GbpLocation,
  type CatalogItem,
  MerchantPost,
  MerchantProduct,
  MerchantProductPromptContext,
  MerchantCategory,
  OnboardingBlueprint,
  TeamMember,
  GbpMediaLocationAssociationCategory,
  Simulation,
  SimulationComponent,
  SimulationComponentReviewStatus,
  SimulationStatus,
} from './use-glammatic-api.d'
import { constructQueryParams, createHeaders, createPayload } from './use-glammatic-api.utils'

const baseUrl = process.env.NEXT_PUBLIC_GLAMMATIC_API_URL || ''
const SERVER_TIMEOUT = 120000 // 2 minutes

// Search Console site setup is a low-rate per-minute quota.
const MAX_CONCURRENT_ROI_FETCHES = 5
const ROI_FETCH_START_GAP_MS = 4_000

export class GlammaticApi {
  private client: AxiosInstance
  private baseUrl: string
  private roiFetches = new ConcurrencyLimit({
    maxConcurrent: MAX_CONCURRENT_ROI_FETCHES,
    gapMs: ROI_FETCH_START_GAP_MS,
  })

  static browserSession() {
    const client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
      xsrfHeaderName: 'X-CSRFToken',
      xsrfCookieName: 'csrftoken',
      withCredentials: true,
      withXSRFToken: true,
      timeout: SERVER_TIMEOUT,
    })
    return new GlammaticApi(client)
  }

  static serverApiKey() {
    const client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': process.env.GLAMMATIC_API_X_KEY!,
      },
      timeout: SERVER_TIMEOUT,
    })
    return new GlammaticApi(client)
  }

  static async serverSession() {
    const { cookies, headers } = await import('next/headers')

    const cookieStore = await cookies()
    const headersStore = await headers()

    const csrf = cookieStore.get('csrftoken')?.value
    const cookieHeader = cookieStore.toString()
    const userAgent = headersStore.get('user-agent') ?? ''

    const client = axios.create({
      baseURL: baseUrl,
      headers: { 'Content-Type': 'application/json' },
    })

    client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      config.headers = {
        ...(config.headers || {}),
        cookie: cookieHeader,
        'user-agent': userAgent,
        ...(csrf ? { 'X-CSRFToken': csrf } : {}),
      } as Any
      return config
    })

    return new GlammaticApi(client)
  }

  constructor(client: AxiosInstance) {
    this.baseUrl = baseUrl
    this.client = client
  }

  getBaseUrl(): string {
    return this.baseUrl
  }

  /**
   * Handles Axios errors.
   * @throws {AxiosError} When the error is not handled by custom logic.
   */
  async handleError(
    error: AxiosError,
    {
      onPostError,
      reportToSentry = true,
    }: { onPostError?: (error: AxiosError) => void; reportToSentry?: boolean } = {}
  ): Promise<never | void> {
    if (isFakeDelays()) {
      await DateUtils.sleep(500)
    }

    const errorCode = getErrorCode(error)

    if (reportToSentry && errorCode && errorCode >= 500) {
      ErrorHandler.handle(error)
    }

    if (onPostError) {
      await onPostError(error)
    }

    throw error
  }

  async handleSuccess<T>(
    response: AxiosResponse<T>,
    { onSuccess }: { onSuccess?: (data: T) => void } = {}
  ): Promise<T> {
    if (isFakeDelays()) {
      await DateUtils.sleep(2000)
    }

    const responseData = objectToCamelCase(_get(response, 'data', {}))
    if (onSuccess) {
      await onSuccess(responseData)
    }
    return responseData
  }

  async get<T = object>(
    path: string,
    config: AxiosRequestConfig = {},
    {
      onSuccess,
      onPostError,
      retries = 3,
      reportToSentry = true,
    }: {
      onSuccess?: (data: T) => void
      onPostError?: (error: AxiosError) => void
      retries?: number
      reportToSentry?: boolean
    } = {}
  ): Promise<T> {
    try {
      let params = objectToSnakeCase((config || {}).params || {})
      params = truncatePayload(params, MAX_GET_PARAM_LENGTH)
      const finalConfig = { ...config, params }

      const response = await retry(() => this.client.get<T>(path, finalConfig), { retries })
      return await this.handleSuccess(response, { onSuccess })
    } catch (error) {
      await this.handleError(error as AxiosError, { onPostError, reportToSentry })
      return null as never
    }
  }

  async post<T = object>(
    path: string,
    data: Any | null = null,
    config: AxiosRequestConfig = {},
    {
      onSuccess,
      onPostError,
      retries = 0,
      reportToSentry = true,
    }: {
      onSuccess?: (data: T) => void
      onPostError?: (error: AxiosError) => void
      retries?: number
      reportToSentry?: boolean
    } = {}
  ): Promise<T> {
    const headers = createHeaders(data)
    const payload = createPayload(data)
    const requestConfig = { ...config, headers }

    try {
      const response = await retry(() => this.client.post<T>(path, payload, requestConfig), {
        retries,
      })
      return await this.handleSuccess(response, { onSuccess })
    } catch (error) {
      await this.handleError(error as AxiosError, { onPostError, reportToSentry })
      return null as never
    }
  }

  async patch<T = object>(
    path: string,
    data: Any,
    config: AxiosRequestConfig = {},
    {
      onSuccess,
      onPostError,
    }: {
      onSuccess?: (data: T) => void
      onPostError?: (error: AxiosError) => void
    } = {}
  ): Promise<T> {
    const headers = createHeaders(data)
    const payload = createPayload(data)
    const requestConfig = { ...config, headers }

    try {
      const response = await this.client.patch(path, payload, requestConfig)
      return await this.handleSuccess(response, { onSuccess })
    } catch (error) {
      await this.handleError(error as AxiosError, { onPostError })
      return null as never
    }
  }

  async delete<T = object>(
    path: string,
    config: AxiosRequestConfig = {},
    {
      onSuccess,
      onPostError,
    }: { onSuccess?: (data: T) => void; onPostError?: (error: AxiosError) => void } = {}
  ): Promise<T> {
    try {
      const response = await this.client.delete(path, config)
      return await this.handleSuccess(response, { onSuccess })
    } catch (error) {
      await this.handleError(error as AxiosError, { onPostError })
      return null as never
    }
  }

  // API Methods

  async me(
    { forceDataRefresh = false }: { forceDataRefresh?: boolean } = {},
    {
      retries = 3,
      reportToSentry = true,
    }: { retries?: number; reportToSentry?: boolean } = {}
  ): Promise<UserAuth> {
    const params = {
      forceDataRefresh,
    }
    return await this.get('/auth/principals/me/', { params }, { retries, reportToSentry })
  }

  async updateUser(
    userId: number,
    {
      email,
      firstName,
      lastName,
      phoneNumber,
    }: {
      email?: string
      firstName?: string
      lastName?: string
      phoneNumber?: string
    } = {}
  ): Promise<User> {
    const payload = {
      ...(email && { email }),
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
      ...(phoneNumber && { phoneNumber }),
    }

    return this.patch(`/auth/principals/${userId}/`, payload)
  }

  async sendEmailVerification(): Promise<void> {
    return await this.post('/auth/principals/send-email-verification/')
  }

  async sendSmsVerification(): Promise<void> {
    return await this.post('/auth/principals/send-sms-verification/')
  }

  async verifySmsCode(code: string): Promise<void> {
    return await this.post('/auth/principals/verify-sms-code/', { code })
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    return await this.post('/auth/principals/send-password-reset-email/', { email })
  }

  async logoutRequest({ returnUrl }: { returnUrl?: string } = {}): Promise<{
    logoutUrl: string
  }> {
    const params = {
      returnUrl: returnUrl || window.location.origin,
    }
    const payload = objectToSnakeCase(params)
    return await this.post('/auth/logout/', payload)
  }

  async scheduledPublish(businessId: number): Promise<void> {
    const payload = {
      merchant: businessId,
    }
    return await this.post(`/merchants/scheduled-publish/`, payload)
  }

  async getMerchantProducts(businessId: number): Promise<MerchantProduct[]> {
    return await this.get(`/merchants/products/`, {
      params: { merchant: businessId },
    })
  }

  async getMerchantProductPromptContexts(
    merchantProductId: number
  ): Promise<MerchantProductPromptContext[]> {
    return await this.get(`/merchants/product-prompt-contexts/`, {
      params: { merchant_product: merchantProductId },
    })
  }

  async patchMerchantProductPromptContext(
    context: MerchantProductPromptContext
  ): Promise<MerchantProductPromptContext> {
    return await this.patch(
      `/merchants/product-prompt-contexts/${context.id}/`,
      context.context ?? {}
    )
  }

  async getMerchantCategories(merchantId: number): Promise<MerchantCategory[]> {
    return await this.get(`/merchants/${merchantId}/categories/`)
  }

  async getOnboardingBlueprint(merchantId: number): Promise<OnboardingBlueprint> {
    return await this.get(`/merchants/${merchantId}/onboarding-blueprints/`)
  }

  async setMerchantCategories(
    merchantId: number,
    categories: Array<{ category: string; subCategory: string }>
  ): Promise<MerchantCategory[]> {
    return await this.post(`/merchants/${merchantId}/categories/set/`, {
      categories,
    })
  }

  async getLocations(businessId: number): Promise<MerchantLocation[]> {
    return await this.get(`/businesses/merchants/${businessId}/locations/`)
  }

  async getReviews(businessId: number): Promise<Review[]> {
    return await this.get(`/businesses/merchants/${businessId}/reviews/`)
  }

  async patchReview(businessId: number, reviewId: number, payload: Review): Promise<Review> {
    return await this.patch(
      `/businesses/merchants/${businessId}/reviews/${reviewId}/`,
      payload
    )
  }

  async patchReviewResponse(
    reviewResponseId: number,
    payload: ReviewResponse
  ): Promise<ReviewResponse> {
    return await this.patch(
      `/merchants/location-review-responses/${reviewResponseId}/`,
      payload
    )
  }

  async publishReviewResponse(reviewResponseId: number | string): Promise<ReviewResponse> {
    return await this.post(
      `/merchants/location-review-responses/${reviewResponseId}/publish/`
    )
  }

  async getWebsiteEvents({
    pageSize = 40,
    status,
    websiteId,
    businessInfoId,
  }: {
    pageSize?: number
    status?: WebsiteEventStatus
    websiteId?: number
    businessInfoId?: number
  } = {}): Promise<PaginatedResponse<WebsiteEvent>> {
    const params = {
      pageSize,
      website: websiteId,
      businessInfo: businessInfoId,
      status,
    }
    const config = { params }

    return await this.get(`/events/website/`, config)
  }

  async patchWebsiteEvent(
    websiteEventId: number | string,
    payload: WebsiteEvent
  ): Promise<WebsiteEvent> {
    return await this.patch(`/events/website/${websiteEventId}/`, payload)
  }

  async getWebsite(websiteId: number): Promise<Website> {
    return await this.get(`/websites/${websiteId}/`)
  }

  async getWebsiteDetail(websiteId: number): Promise<WebsiteDetail> {
    return await this.get(`/websites/${websiteId}/details`)
  }

  async getWebsiteEventFileUrl(
    websiteEventId: number | string,
    fileUuid: string
  ): Promise<{ url: string }> {
    return await this.get<{ url: string }>(
      `/events/website/${websiteEventId}/files/${fileUuid}/`
    )
  }

  async getWebsitePages(websiteId: number): Promise<WebsitePage[]> {
    return await this.get(`/websites/${websiteId}/pages/`)
  }

  async createWebsitePage(
    websiteId: number,
    payload: { title: string; slug: string; pageType: string; navigationPriority: number }
  ): Promise<WebsitePage> {
    return await this.post(`/websites/${websiteId}/pages/`, payload)
  }

  async getQuestionGroups(merchantProductId: number): Promise<QuestionGroup[]> {
    return await this.get(`/question-groups/`, {
      params: { merchantProduct: merchantProductId },
    })
  }

  async patchQuestion(questionId: number, payload: { text: string }): Promise<Any> {
    return await this.patch(`/questions/${questionId}/`, payload)
  }

  async patchAnswer(answerId: number, payload: { text: string }): Promise<Any> {
    return await this.patch(`/answers/${answerId}/`, payload)
  }

  async deleteQuestion(questionId: number): Promise<void> {
    await this.delete(`/questions/${questionId}/`)
  }

  async createQuestion(
    merchantProductId: number,
    questionGroupId: number,
    data: { text?: string; answers?: { text?: string; isPreferred?: boolean }[] }
  ): Promise<QuestionGroupQuestion> {
    const answers = (data.answers ?? [{ text: '', isPreferred: true }]).map(a => ({
      text: a.text ?? '',
      isPreferred: a.isPreferred ?? true,
    }))
    return await this.post(`/questions/`, {
      merchantProduct: merchantProductId,
      groups: [{ id: questionGroupId }],
      text: data.text ?? '',
      answers,
    })
  }

  async getBusinesses({
    search,
    pageSize = 20,
    userId,
    type = 'merchant',
  }: {
    search?: string
    pageSize?: number
    userId?: number
    type?: string
  } = {}): Promise<PaginatedResponse<Business>> {
    const params = {
      query: search,
      pageSize,
      v_1: true,
      userId,
      type,
    }
    const config = { params }

    const response = await this.get<PaginatedResponse<Business>>(`/businesses/list/`, config)

    const { results } = response
    const filteredResults = results.filter(({ type }: Business) => type === 'merchant')

    return { ...response, results: filteredResults }
  }

  async getCatalog(businessId: number): Promise<Catalog> {
    return await this.get(`/businesses/merchants/${businessId}/menu/`)
  }

  async patchCatalogCategory(
    businessId: number,
    categoryId: number,
    payload: CatalogCategory
  ): Promise<CatalogCategory> {
    return await this.patch(
      `/businesses/merchants/${businessId}/menu/categories/${categoryId}/`,
      payload
    )
  }

  async patchCatalogItem(
    businessId: number,
    itemId: number,
    payload: CatalogItem
  ): Promise<CatalogItem> {
    return await this.patch(
      `/businesses/merchants/${businessId}/menu/items/${itemId}/`,
      payload
    )
  }

  async createBusinessFile(
    businessId: number,
    payload: {
      file: File
      filename: string
      description: string
      source: string
      fileType?: string // extension with '.'
    },
    {
      onUploadProgress,
    }: { onUploadProgress?: (progressEvent: AxiosProgressEvent) => void } = {}
  ): Promise<BusinessFile> {
    return await this.post(`/businesses/merchants/${businessId}/files/`, payload, {
      onUploadProgress,
    })
  }

  async getBusinessFiles(businessId: number): Promise<BusinessFile[]> {
    return await this.get(`/businesses/merchants/${businessId}/files/`)
  }

  async getProviderServiceSettings(businessId: number): Promise<ProviderServiceSetting[]> {
    return await this.get(`/integrations/merchants/${businessId}/`)
  }

  async getProviderServiceSetting(
    businessId: number,
    providerServiceSettingId: number
  ): Promise<ProviderServiceSetting> {
    return await this.get(
      `/integrations/merchants/${businessId}/${providerServiceSettingId}/`
    )
  }

  async deleteProviderServiceSetting(
    businessId: number,
    providerServiceSettingId: number
  ): Promise<void> {
    return await this.delete(
      `/integrations/merchants/${businessId}/${providerServiceSettingId}/`
    )
  }

  async updateProviderServiceSetting(
    businessId: number,
    providerServiceSettingId: number,
    payload: ProviderServiceSetting
  ): Promise<ProviderServiceSetting> {
    return await this.patch(
      `/integrations/merchants/${businessId}/${providerServiceSettingId}/`,
      payload
    )
  }

  async getComponentConfigurations(websiteId: number): Promise<ComponentConfiguration[]> {
    return await this.get(`/websites/component-configurations/`, {
      params: { website: websiteId },
    })
  }

  async getRoiReport(websiteId: number): Promise<RoiReport> {
    return await this.roiFetches.run(() =>
      this.get(`/websites/${websiteId}/roi/`, {}, { retries: 0 })
    )
  }

  async getContentSuggestions(
    websiteId: number,
    { reviewed = false }: { reviewed?: boolean } = {}
  ): Promise<ContentSuggestion[]> {
    const params = {
      websiteId,
      reviewed,
    }
    return await this.get(`/content-suggestions/`, { params })
  }

  async reviewContentSuggestion(
    suggestionId: number,
    {
      approved,
      reason = '',
      acceptedValue = '',
    }: {
      approved?: boolean
      reason?: string
      acceptedValue?: string
    }
  ): Promise<ContentSuggestion> {
    const finalPayload = {
      approved,
      reason,
      acceptedValue,
    }

    return await this.post(`/content-suggestions/${suggestionId}/review/`, finalPayload)
  }

  async getStockImages({
    query,
    page,
  }: {
    query: string
    page: number
  }): Promise<StockImage[]> {
    try {
      return await this.get(`/stock-images/?query=${query}&page=${page}`)
    } catch {
      return []
    }
  }

  async getTeamMembers(businessId: number): Promise<TeamMember[]> {
    return await this.get(`businesses/merchants/${businessId}/team-members/`)
  }

  async getGbpAuthUrl(businessId: number, goBackUri: string): Promise<{ url: string }> {
    const params = {
      goBackUri,
    }
    return await this.get(`/integrations/gmb/merchants/${businessId}/oauth/`, { params })
  }

  async disconnectGbp(businessId: number): Promise<void> {
    return await this.post(`/integrations/gmb/merchants/${businessId}/oauth/cancel/`)
  }

  async getGbpAccounts(businessId: number): Promise<GbpAccount[]> {
    return await this.get(`/integrations/gmb/merchants/${businessId}/accounts/`)
  }

  async getGbpLocations(businessId: number, accountId: string): Promise<GbpLocation[]> {
    return await this.get(
      `/integrations/gmb/merchants/${businessId}/accounts/${accountId}/locations/`
    )
  }

  async createGbpMedia(
    providerSettingId: number,
    locationId: number,
    businessFileId: number,
    metadata: {
      locationAssociationCategory: GbpMediaLocationAssociationCategory
      description: string
    }
  ): Promise<void> {
    const payload = {
      providerSetting: providerSettingId,
      location: locationId,
      businessFile: businessFileId,
      metadata,
    }
    return await this.post(`/businesses/business-file-provider-metadata/`, payload)
  }

  async associateGbpLocation(
    businessId: number,
    locations: {
      fishermanLocationId: number
      gbbAccountId: string
      gbbLocationId: string
    }[]
  ): Promise<ProviderServiceSetting[]> {
    const payload = (locations || []).map(location => ({
      fishermanLocationId: location.fishermanLocationId,
      gmbAccountId: location.gbbAccountId,
      gmbLocationId: location.gbbLocationId,
    }))

    return await this.post(`/integrations/gmb/merchants/${businessId}/location/`, payload)
  }

  async createPost(businessId: number, payload: MerchantPost): Promise<MerchantPost> {
    return await this.post(`/businesses/merchants/${businessId}/post/`, payload)
  }

  async scheduleTasksCreationCronJob(businessId: number): Promise<void> {
    return await this.post(`/tasks/${businessId}/task/cron/create/`)
  }

  async provisionTasks(businessId: number): Promise<void> {
    return await this.post(`/tasks/${businessId}/task/provision/`, null, {}, { retries: 2 })
  }

  async patchPost(
    businessId: number,
    postId: number,
    payload: MerchantPost
  ): Promise<MerchantPost> {
    return await this.patch(`/businesses/merchants/${businessId}/post/${postId}/`, payload)
  }

  async textCompletion(
    businessId: number,
    promptClass: string,
    params = {} as Record<string, Any>
  ): Promise<{ textCompletion: string | Record<string, Any> }> {
    const payload = objectToSnakeCase({
      businessType: 'merchant',
      businessId,
      promptClass,
      ...params,
    })
    return await this.post(`/text-completion/`, payload)
  }

  async listSimulations({
    context,
    status,
  }: {
    context?: number
    status?: SimulationStatus | SimulationStatus[]
  } = {}): Promise<Simulation[]> {
    const params = {
      ...(context !== undefined && { context }),
      ...(status !== undefined && { status }),
    }
    return await this.get('/merchants/product-prompt-contexts/simulations/', { params })
  }

  async createSimulation(contextId: number): Promise<Simulation> {
    return await this.post('/merchants/product-prompt-contexts/simulations/', {
      context: contextId,
    })
  }

  async deleteSimulation(simulationId: number): Promise<void> {
    return await this.delete(
      `/merchants/product-prompt-contexts/simulations/${simulationId}/`
    )
  }

  async approveSimulation(
    simulationId: number,
    { feedback }: { feedback?: string } = {}
  ): Promise<Simulation> {
    return await this.post(
      `/merchants/product-prompt-contexts/simulations/${simulationId}/approve/`,
      { ...(feedback !== undefined && { feedback }) }
    )
  }

  async rejectSimulation(simulationId: number, feedback: string): Promise<Simulation> {
    return await this.post(
      `/merchants/product-prompt-contexts/simulations/${simulationId}/reject/`,
      { feedback }
    )
  }

  async patchSimulationComponent(
    componentId: number,
    {
      reviewStatus,
      feedback,
    }: { reviewStatus: SimulationComponentReviewStatus; feedback?: string }
  ): Promise<SimulationComponent> {
    return await this.patch(
      `/merchants/product-prompt-contexts/simulations/components/${componentId}/`,
      { reviewStatus, ...(feedback !== undefined && { feedback }) }
    )
  }

  // Admin only.
  async regenerateWebsiteContent(websiteId: number): Promise<void> {
    return await this.post(`/websites/${websiteId}/regenerate-content/`)
  }
}

export function glammaticApiRedirect(
  path: string,
  {
    redirectUrl = null,
    queryParams = {},
  }: {
    redirectUrl?: string | null
    queryParams?: Record<string, Any>
  } = {}
): void {
  const baseURL = process.env.NEXT_PUBLIC_GLAMMATIC_API_URL
  const currentAppUrl = redirectUrl || window.location.origin

  const allQueryParams: Record<string, Any> = {
    redirect_url: currentAppUrl,
    ...queryParams,
  }

  const queryString = constructQueryParams(allQueryParams)
  const redirectTo = `${baseURL}${path}${queryString ? `?${queryString}` : ''}`
  window.location.href = redirectTo
}
